import { Component, ViewChild, HostListener, OnDestroy, OnInit } from '@angular/core';
import { ToastController, LoadingController } from '@ionic/angular';
import { BaseClass } from 'src/app/shared/classes/base';
import { TranslationService } from 'src/app/shared/services/translation.service';
import { Router } from '@angular/router';
import { Note } from './_interfaces/note.interface';
import { NotepadService } from './notepad.service';
import { AuthService, UserItem } from '../shared/services/auth.service';
import { SubordinateNotes } from './_interfaces/subordinate-notes.interface';
import { SubordinatesNotesComponent } from './subordinates-notes/subordinates-notes.component';
import { SquareSubordinatesNotesComponent } from './square-subordinates-notes/square-subordinates-notes.component';
import { ConfigService } from '../shared/services/multi-clients/config.service';

@Component({
  selector: 'app-notepad',
  templateUrl: './notepad.page.html',
  styleUrls: ['./notepad.page.scss']
})
export class NotepadPage extends BaseClass implements OnDestroy, OnInit {

  @ViewChild('subordinates', { static: false }) subordinates: SubordinatesNotesComponent;
  @ViewChild('allSubordinates', { static: false }) allSubordinates: SquareSubordinatesNotesComponent;

  public envNotePadWithAllUsers: boolean;

  public currentSegment: 'user' | 'subordinates' = 'user';
  public currentUser: UserItem;
  public notes: Array<Note>;
  public allNotes: Array<Note>;
  public subordinateNotes: Array<SubordinateNotes>;
  public allSubordinateNotes: Array<SubordinateNotes>;
  public currentSegmentForAll: 'user' | 'allSubordinates' = 'user';

  private _returnPath: string = undefined;

  constructor(
    protected _toastController: ToastController,
    protected _translationService: TranslationService,
    private _loadingController: LoadingController,
    private _router: Router,
    private _notepadService: NotepadService,
    private _authService: AuthService,
    private _configService: ConfigService
  ) {
    super(_toastController, _translationService);
  }

  ngOnInit() {
    this.envNotePadWithAllUsers = this._configService.settings.features.notePadWithAllUsers;
  }

  ionViewWillEnter() {
    this.currentSegment = 'user';
    this.loadBothNotes();
    this._authService.getUserData().subscribe(
      (response) => this.currentUser = response.data
    );
  }

  public async loadBothNotes(): Promise<void> {
    const loading = await this._loadingController.create();
    await loading.present();

    try {
      this.loadNotes(loading);
      if (this.envNotePadWithAllUsers)
        this.loadAllSubordinatesNotes(loading);
      loading.dismiss();
    } catch (error) {
      loading.dismiss();
      this.notifyError(error);
    }
  }

  public loadNotes(loading: HTMLIonLoadingElement) {
    this._notepadService.getUserNotes().subscribe((response) => {
      this.notes = response.data;

      if (this.envNotePadWithAllUsers) {
        this.loadSubordinatesNotes(loading);
        this.loadAllSubordinatesNotes(loading);
      }
      else
        this.loadSubordinatesNotes(loading);
    });
  }

  public async loadSubordinatesNotes(loading: HTMLIonLoadingElement) {
    this._notepadService.getSubordinatesNotes().subscribe(async (response) => {
      this.subordinateNotes = response.data;
      if (this.subordinates) {
        this.subordinates.updateSelectedUser();
        this._setReturnPathAndSubordinateNote();
      }
    }, (error) => {
      this.notifyError(error);
    });
  }

  private _setReturnPathAndSubordinateNote(): void {
    if (this._returnPath && this._returnPath.includes('manager-evaluation')) {
      this.currentSegment = 'subordinates';
      const userId = this._returnPath.split('/')[2];
      const user = this.subordinateNotes.find(s => s.id === userId);
      setTimeout(() => this.subordinates.selectUser(user));
    }
  }

  public loadAllSubordinatesNotes(loading: HTMLIonLoadingElement): void {
    this._notepadService.getAllSubordinatesNotes().subscribe((response) => {
      this.allSubordinateNotes = response.data;

      if (this.allSubordinates) {
        this.allSubordinates.updateSelectedUser();
        this._setReturnPathAndAllSubordinateNote();
      }
    }, (error) => {
      this.notifyError(error);
    });
  }

  private _setReturnPathAndAllSubordinateNote(): void {
    if (this._returnPath && this._returnPath.includes('manager-evaluation')) {
      this.currentSegmentForAll = 'allSubordinates';
      const userId = this._returnPath.split('/')[2];
      const user = this.allSubordinateNotes.find(s => s.id === userId);
      setTimeout(() => this.allSubordinates.selectUser(user));
    }
  }

  public goBack(): void {
    this._returnPath = localStorage.getItem('Notepad_ReturnPath');
    this._router.navigate([
      this._returnPath ? this._returnPath : 'dashboard'
    ]);
  }
  
  @HostListener('window:beforeunload')
  ngOnDestroy() {
    localStorage.removeItem('Notepad_ReturnPath');
  }

}
